import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Divider,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
  alpha,
  CircularProgress,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoIcon from '@mui/icons-material/Info';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined';
import { FormFieldRenderer } from '@shared/forms/components/fields';
import { useSubjectLanguage } from '@features/user';
import { useFormById, useFormDraftById } from '@features/forms';

interface FormPreviewProps {
  formId: string;
  draft?: boolean;
}

/**
 * Renders a preview of a form definition with disabled form controls
 * @param formId - The ID of the form to preview
 * @param draft - If true, fetches the draft version of the form
 */
const FormPreview: React.FC<FormPreviewProps> = ({ formId, draft = false }) => {
  const theme = useTheme();
  const subjectLanguage = useSubjectLanguage();

  // Use appropriate hook based on draft prop
  const publishedResult = useFormById(formId, { skip: draft });
  const draftResult = useFormDraftById(formId, { skip: !draft });

  // Select the appropriate result
  const {
    form: formDefinition,
    isLoading: loading,
    isError,
    error,
  } = draft ? draftResult : publishedResult;

  // Convert hook error state to string for compatibility
  const errorMessage = isError ? 'Failed to load form definition' : null;

  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});

  // Initialize expanded groups when form definition is loaded
  // Use form_id as dependency to prevent infinite loops
  useEffect(() => {
    if (formDefinition && formDefinition.groups.length > 0) {
      setExpandedGroups(
        formDefinition.groups.reduce(
          (acc, _, index) => {
            acc[index.toString()] = index === 0; // Expand only the first group initially
            return acc;
          },
          {} as Record<string, boolean>,
        ),
      );
    }
  }, [formDefinition?.form_id, formDefinition?.groups.length]);

  const handleGroupChange =
    (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedGroups({
        ...expandedGroups,
        [panel]: isExpanded,
      });
    };

  const renderField = (field: any) => {
    return <FormFieldRenderer field={field} mode="preview" sx={{ mb: 2 }} />;
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          p: 4,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (errorMessage || !formDefinition) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          p: 4,
        }}
      >
        <ErrorOutlineIcon color="error" sx={{ fontSize: 48, mb: 2 }} />
        <Typography variant="h6" color="error" gutterBottom>
          {errorMessage || 'Failed to load form'}
        </Typography>
        <Typography color="text.secondary">
          Please try again later or contact support if the problem persists.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Paper elevation={0} sx={{ p: 3, mb: 3, bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
        <Typography variant="h5" gutterBottom>
          {formDefinition.title}
        </Typography>

        <Typography variant="body1" color="text.secondary" gutterBottom>
          {formDefinition.description}
        </Typography>

        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
          <InfoIcon fontSize="small" color="info" sx={{ mr: 1 }} />
          <Typography variant="body2" color="info.main">
            This is a preview of how the form will appear to {subjectLanguage}s. Form fields are
            disabled in preview mode.
          </Typography>
        </Box>
      </Paper>

      {formDefinition.groups.map((group, groupIndex) => (
        <Accordion
          key={groupIndex}
          expanded={expandedGroups[groupIndex.toString()] || false}
          onChange={handleGroupChange(groupIndex.toString())}
          sx={{
            mb: 2,
            boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.05)',
            '&:before': {
              display: 'none',
            },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: alpha(theme.palette.primary.main, 0.05),
              '& .MuiAccordionSummary-content': {
                margin: '12px 0',
              },
            }}
          >
            <Box>
              <Typography variant="h6">{group.title}</Typography>
              {group.description && (
                <Typography variant="body2" color="text.secondary">
                  {group.description}
                </Typography>
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 3 }}>
            {group.sections.map((section, sectionIndex) => (
              <Box
                key={sectionIndex}
                sx={{ mb: sectionIndex === group.sections.length - 1 ? 0 : 4 }}
              >
                {section.title && (
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: "bold" }}>
                    {section.title}
                  </Typography>
                )}
                {section.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {section.description}
                  </Typography>
                )}

                <Grid container spacing={2}>
                  {section.fields.map((field, fieldIndex) => (
                    <Grid size={{ xs: 12 }} key={fieldIndex}>
                      {renderField(field)}
                    </Grid>
                  ))}
                </Grid>

                {sectionIndex < group.sections.length - 1 && <Divider sx={{ my: 3 }} />}
              </Box>
            ))}
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

export default FormPreview;
